import AdminLayout from '@/components/admin/AdminLayout';
import BlogPostPreview from '@/components/admin/BlogPostPreview';
import RichTextEditor from '@/components/admin/RichTextEditor';
import AuthRequired from '@/components/auth/AuthRequired';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { suggestExcerpt, suggestMetaDescription, suggestSlug, suggestTags } from '@/utils/blog/aiSuggestions';
import { generateSlug, makeSlugUnique } from '@/utils/blog/slugUtils';
import { debounce } from 'lodash';
import { ChevronLeft, LucideIcon, Save, Tag, Wand } from 'lucide-react';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Add custom styles to ensure buttons are visible
const customStyles = `
  .card-footer-buttons {
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    background-color: #fff;
    border-top: 1px solid #e2e8f0;
    z-index: 10;
    position: relative;
  }
  
  .admin-button {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 0.375rem !important;
    font-weight: 500 !important;
    padding: 0.5rem 1rem !important;
    transition: all 0.2s !important;
    cursor: pointer !important;
    background-color: #fff !important;
    color: #374151 !important;
    border: 1px solid #d1d5db !important;
    font-size: 0.875rem !important;
    line-height: 1.25rem !important;
    height: auto !important;
    width: auto !important;
    opacity: 1 !important;
    visibility: visible !important;
    position: relative !important;
    z-index: 50 !important;
    margin: 0 !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  }
  
  .admin-button svg {
    width: 14px !important;
    height: 14px !important;
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  .admin-button span {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  .admin-button-primary {
    background-color: #2563eb !important;
    color: white !important;
    border-color: #2563eb !important;
  }
  
  .admin-button-primary:hover {
    background-color: #1d4ed8 !important;
  }
  
  .admin-button-outline {
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;
  }
  
  .admin-button-outline:hover {
    background-color: #f9fafb !important;
  }
  
  .admin-button:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }
  
  /* Dark mode styles */
  [data-theme="dark"] .card-footer-buttons {
    background-color: #1e293b;
    border-color: #334155;
  }
  
  [data-theme="dark"] .admin-button-primary {
    background-color: #3b82f6 !important;
    border-color: #3b82f6 !important;
  }
  
  [data-theme="dark"] .admin-button-primary:hover {
    background-color: #2563eb !important;
  }
  
  [data-theme="dark"] .admin-button-outline {
    background-color: #1e293b !important;
    border-color: #475569 !important;
    color: #e2e8f0 !important;
  }
  
  [data-theme="dark"] .admin-button-outline:hover {
    background-color: #334155 !important;
  }

  /* Add specific styles for suggestion buttons */
  button[type="button"].admin-button-outline {
    display: inline-flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    z-index: 100 !important;
    pointer-events: auto !important;
  }

  /* Fix for Wand icon and text in suggestion buttons */
  button[type="button"].admin-button-outline svg,
  button[type="button"].admin-button-outline span {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    z-index: 101 !important;
  }

  /* Ensure the suggestion buttons are clickable */
  .flex.items-center.justify-between button[type="button"].admin-button-outline {
    pointer-events: auto !important;
    user-select: auto !important;
    cursor: pointer !important;
  }
`;

// Add custom styles for the spin animation
const keyframesStyle = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Add custom styles to ensure buttons are visible
const tagButtonStyles = `
  .tag-button {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.5rem !important;
    background-color: #EFF6FF !important;
    color: #1D4ED8 !important;
    border-color: #BFDBFE !important;
  }
  
  .tag-button:hover {
    background-color: #DBEAFE !important;
    color: #1E40AF !important;
  }
  
  .dark .tag-button {
    background-color: rgba(30, 58, 138, 0.2) !important;
    color: #93C5FD !important;
    border-color: rgba(30, 58, 138, 0.3) !important;
  }
  
  .dark .tag-button:hover {
    background-color: rgba(30, 58, 138, 0.3) !important;
  }
`;

interface BlogPostFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string[];
  image: string;
  author: string;
  tags: string;
  meta_description: string;
}

interface SuggestionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
  text?: string;
}

// Create a custom SuggestionButton component with forwardRef
const SuggestionButton = React.forwardRef<HTMLButtonElement, SuggestionButtonProps>(({ 
  onClick, 
  disabled = false, 
  isLoading = false, 
  icon: Icon = Wand, 
  text = "Sugerir" 
}, ref) => {
  const buttonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    visibility: 'visible',
    opacity: disabled ? 0.5 : 1,
    position: 'relative',
    zIndex: 100
  };

  const iconStyle: CSSProperties = {
    display: 'inline-block',
    visibility: 'visible',
    opacity: 1,
    width: '14px',
    height: '14px'
  };

  const textStyle: CSSProperties = {
    display: 'inline-block',
    visibility: 'visible',
    opacity: 1
  };

  const spinnerStyle: CSSProperties = {
    animation: 'spin 1s linear infinite',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    borderTop: '2px solid currentColor',
    borderRight: '2px solid transparent',
    borderBottom: '2px solid currentColor',
    borderLeft: '2px solid transparent',
    marginRight: '4px'
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={buttonStyle}
      ref={ref}
    >
      {isLoading ? (
        <div style={spinnerStyle}></div>
      ) : (
        <Icon style={iconStyle} />
      )}
      <span style={textStyle}>{text}</span>
    </button>
  );
});

// Add display name for better debugging
SuggestionButton.displayName = 'SuggestionButton';

export const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('editor');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isEditMode = !!id;
  const isCloneMode = window.location.pathname.includes('/clone/');

  // Initialize form data with all required fields
  const [formData, setFormData] = useState<BlogPostFormData>({
    title: '',
    slug: '',
    excerpt: '',
    content: [''],
    image: '',  // Default empty image URL
    author: 'Admin',  // Default author
    tags: '',
    meta_description: ''
  });

  // State for AI suggestion loading
  const [isGeneratingAI, setIsGeneratingAI] = useState({
    excerpt: false,
    tags: false,
    metaDescription: false
  });

  const slugChangeTimeout = useRef<NodeJS.Timeout | null>(null);

  const debouncedValidateSlug = useCallback(debounce(async (slug: string) => {
    if (!slug) {
      return;
    }
    
    try {
      const exists = await makeSlugUnique(slug, isEditMode ? id : undefined);
      
      if (exists !== slug) {
        toast({
          title: 'Este slug já existe',
          description: 'Sugerimos: ' + exists,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Slug disponível',
          variant: 'default',
        });
        
        setFormData(prev => ({ ...prev, slug }));
      }
    } catch (error) {
      toast({
        title: 'Erro ao validar slug',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    }
  }, 500), [id, isEditMode, toast]);

  const debouncedUpdateSlugFromTitle = useCallback(debounce(async (title: string) => {
    if (!title) return;
    
    try {
      // First, set a basic slug as a fallback
      const basicSlug = generateSlug(title);
      setFormData(prev => ({ ...prev, slug: basicSlug }));
      
      // Try to get content for context if available
      let contentText = '';
      if (formData.content && formData.content.length > 0) {
        contentText = formData.content
          .map(html => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            return tempDiv.textContent || tempDiv.innerText || '';
          })
          .join('\n\n');
      }
      
      // Try to get an AI-generated SEO slug
      try {
        const seoSlug = await suggestSlug(title, contentText);
        
        if (seoSlug) {
          // Update with the SEO-optimized slug
          setFormData(prev => ({ ...prev, slug: seoSlug }));
          
          // Silently check if the slug is unique
          makeSlugUnique(seoSlug, isEditMode ? id : undefined)
            .then(uniqueSlug => {
              if (uniqueSlug !== seoSlug) {
                // If the slug needed to be made unique, update it silently
                setFormData(prev => ({ ...prev, slug: uniqueSlug }));
              }
            })
            .catch(error => {
              console.error('Error checking slug uniqueness:', error);
            });
          
          console.log('Generated SEO-optimized slug:', seoSlug);
          return;
        }
      } catch (error) {
        console.error('Error generating SEO slug, falling back to basic slug:', error);
      }
      
      // If AI generation fails, silently check if the basic slug is unique
      makeSlugUnique(basicSlug, isEditMode ? id : undefined)
        .then(uniqueSlug => {
          if (uniqueSlug !== basicSlug) {
            // If the slug needed to be made unique, update it silently
            setFormData(prev => ({ ...prev, slug: uniqueSlug }));
          }
        })
        .catch(error => {
          console.error('Error checking slug uniqueness:', error);
        });
      
    } catch (error) {
      console.error('Error in slug generation:', error);
      
      // Ultimate fallback - generate a basic slug
      const fallbackSlug = generateSlug(title);
      setFormData(prev => ({ ...prev, slug: fallbackSlug }));
      
      // Silently check if the fallback slug is unique
      makeSlugUnique(fallbackSlug, isEditMode ? id : undefined)
        .then(uniqueSlug => {
          if (uniqueSlug !== fallbackSlug) {
            // If the slug needed to be made unique, update it silently
            setFormData(prev => ({ ...prev, slug: uniqueSlug }));
          }
        })
        .catch(error => {
          console.error('Error checking slug uniqueness:', error);
        });
    }
  }, 700), [formData.content, id, isEditMode]);

  useEffect(() => {
    if (id) {
      fetchBlogPost();
    } else if (!isCloneMode) {
      setInitialLoading(false);
      if (titleInputRef.current) {
        titleInputRef.current.focus();
      }
    }
  }, [id]);

  const fetchBlogPost = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        // Process content to ensure it's an array of strings
        const processedContent = Array.isArray(data.content) 
          ? data.content.map(item => typeof item === 'string' ? item : JSON.stringify(item))
          : [data.content?.toString() || ''];

        // If in clone mode, modify title and slug
        let title = data.title || '';
        let slug = data.slug || '';
        
        if (isCloneMode) {
          title = `${title} (Clonado)`;
          slug = `${slug}-clonado`;
          
          // Ensure slug is unique
          try {
            slug = await makeSlugUnique(slug);
          } catch (error) {
            console.error('Error making cloned slug unique:', error);
            // Fallback to basic slug with timestamp
            slug = `${slug}-${Date.now()}`;
          }
        }

        setFormData({
          title: title,
          slug: slug,
          excerpt: data.excerpt || '',
          content: processedContent,
          image: data.image || '',
          author: data.author || 'Admin',
          tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
          meta_description: data.meta_description || ''
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar post',
        description: error.message,
        variant: 'destructive',
      });
      navigate('/admin/blog');
    } finally {
      setInitialLoading(false);
    }
  };

  // Helper function to capitalize the first letter of each word in a string
  const capitalizeWords = (text: string): string => {
    if (!text) return '';
    
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Enforce character limit for meta_description
    if (name === 'meta_description' && value.length > 150) {
      setFormData(prev => ({ ...prev, [name]: value.substring(0, 150) }));
      
      // Show a toast notification about the limit
      toast({
        title: 'Limite de caracteres',
        description: 'A meta descrição não pode exceder 150 caracteres.',
        variant: 'destructive',
      });
      return;
    }
    
    // For tags field, capitalize each tag
    if (name === 'tags') {
      const capitalizedTags = value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '')
        .map(tag => capitalizeWords(tag))
        .join(', ');
      
      setFormData(prev => ({ ...prev, [name]: capitalizedTags }));
    } else if (name !== 'slug') { // Prevent direct editing of slug field
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (name === 'slug') {
      debouncedValidateSlug(value);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, title: value }));
    
    // Always update the slug when the title changes
    debouncedUpdateSlugFromTitle(value);
  };

  const handleTagClick = (tag: string) => {
    // Get current tags as an array
    const currentTags = formData.tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t !== '');
    
    // Remove the tag
    const updatedTags = currentTags.filter(t => t !== tag).join(', ');
    setFormData(prev => ({ ...prev, tags: updatedTags }));
  };

  const handleContentChange = (newContent: string[]) => {
    // Validate content
    if (!Array.isArray(newContent)) {
      return;
    }
    
    // Check for empty content
    if (newContent.length === 0) {
      newContent = ['<p></p>'];
    }
    
    // Check for invalid HTML
    for (let i = 0; i < newContent.length; i++) {
      if (typeof newContent[i] !== 'string') {
        newContent[i] = '<p>Invalid content</p>';
      }
    }
    
    setFormData(prev => {
      return { ...prev, content: newContent };
    });
  };

  /**
   * Helper function to clean quotation marks from text
   */
  const cleanQuotes = (text: string, maxLength?: number): string => {
    if (!text) return '';
    
    // Remove aspas no início e fim da string
    let cleaned = text.trim();
    
    // Remove aspas duplas no início e fim
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.substring(1, cleaned.length - 1).trim();
    }
    
    // Remove aspas simples no início e fim
    if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
      cleaned = cleaned.substring(1, cleaned.length - 1).trim();
    }
    
    // Remove aspas triplas (markdown) no início e fim
    if (cleaned.startsWith('```') && cleaned.endsWith('```')) {
      cleaned = cleaned.substring(3, cleaned.length - 3).trim();
    }
    
    // Remove frases introdutórias comuns
    cleaned = cleaned.replace(/^(aqui está|segue|segue abaixo|aqui vai|sugestão|sugestões)\s*:?\s*/i, '');
    
    // Remove aspas dentro do texto (para tags)
    cleaned = cleaned.replace(/["']/g, '');
    
    // Apply max length if specified
    if (maxLength && cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength);
    }
    
    return cleaned.trim();
  };

  const generateAISuggestion = async (type: 'excerpt' | 'tags' | 'metaDescription') => {
    // Verificar se há conteúdo suficiente
    if (!formData.content || !formData.title) {
      toast({
        title: "Conteúdo insuficiente",
        description: "É necessário ter título e conteúdo para gerar sugestões.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Extrair texto do conteúdo HTML
      const tempDiv = document.createElement('div');
      // Verificar se content é um array ou string
      const contentHtml = Array.isArray(formData.content) 
        ? formData.content.join('\n\n') 
        : formData.content;
      
      tempDiv.innerHTML = contentHtml;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Verificar se há texto suficiente
      if (textContent.trim().length < 50) {
        toast({
          title: "Conteúdo insuficiente",
          description: "O conteúdo do post precisa ter pelo menos 50 caracteres para gerar sugestões.",
          variant: "destructive",
        });
        return;
      }

      // Mostrar toast de carregamento
      toast({
        title: `Gerando ${type === 'excerpt' ? 'resumo' : type === 'tags' ? 'tags' : 'meta descrição'}...`,
        description: "Isso pode levar alguns segundos.",
      });

      // Gerar sugestão com base no tipo
      if (type === 'excerpt') {
        console.log('Gerando sugestão de resumo...');
        const excerpt = await suggestExcerpt(textContent, formData.title);
        if (excerpt) {
          setFormData(prev => ({ ...prev, excerpt }));
          toast({
            title: "Resumo gerado com sucesso!",
            description: "O resumo foi atualizado com a sugestão da IA.",
          });
        }
      } else if (type === 'tags') {
        console.log('Gerando sugestão de tags...');
        const tags = await suggestTags(textContent, formData.title);
        if (tags && tags.length > 0) {
          // Converter array de tags para string separada por vírgulas
          const tagsString = tags.join(', ');
          setFormData(prev => ({ ...prev, tags: tagsString }));
          toast({
            title: "Tags geradas com sucesso!",
            description: `Sugestões: ${tagsString}`,
          });
        } else {
          toast({
            title: "Nenhuma tag gerada",
            description: "Não foi possível gerar tags relevantes. Tente adicionar mais conteúdo ao post.",
            variant: "destructive",
          });
        }
      } else if (type === 'metaDescription') {
        console.log('Gerando sugestão de meta descrição...');
        const metaDescription = await suggestMetaDescription(textContent, formData.title);
        if (metaDescription) {
          setFormData(prev => ({ ...prev, meta_description: metaDescription }));
          toast({
            title: "Meta descrição gerada com sucesso!",
            description: "A meta descrição foi atualizada com a sugestão da IA.",
          });
        }
      }
    } catch (error) {
      console.error(`Error generating ${type}:`, error);
      
      // Verificar se é um erro de rate limit
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('429') || errorMessage.toLowerCase().includes('rate limit')) {
        toast({
          title: "Limite de requisições excedido",
          description: "O serviço de IA está temporariamente indisponível devido ao alto volume de requisições. Por favor, tente novamente em alguns minutos.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: `Erro ao gerar ${type === 'excerpt' ? 'resumo' : type === 'tags' ? 'tags' : 'meta descrição'}`,
          description: errorMessage.substring(0, 100),
          variant: "destructive",
        });
      }
    }
  };

  const convertContentToJsonb = (content: string[]) => {
    try {
      // If content is already HTML, return it as is
      return content.map((para, index) => {
        // Clean up any potential issues with the HTML
        if (!para || para.trim() === '') {
          return '<p></p>';
        }
        
        // If it's already HTML, return it as is
        if (para.startsWith('<') && para.endsWith('>')) {
          return para;
        }
        
        // Otherwise, wrap it in paragraph tags
        return `<p>${para.replace(/\n/g, '<br/>')}</p>`;
      });
    } catch (error) {
      // Return a fallback in case of error
      return ['<p>Error processing content</p>'];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Generate a unique slug if not in edit mode or if in clone mode
      const uniqueSlug = await makeSlugUnique(formData.slug, isEditMode && !isCloneMode ? id : undefined);
      
      // Process tags into an array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '');

      // Convert content to proper format
      const contentJsonb = convertContentToJsonb(formData.content);

      // Prepare post data with all required fields
      const postData = {
        title: formData.title,
        slug: uniqueSlug,
        excerpt: formData.excerpt,
        content: contentJsonb,
        image: formData.image || '',  // Ensure image is never null
        author: formData.author || 'Admin',  // Ensure author is never null
        tags: tagsArray,
        meta_description: formData.meta_description,
        updated_at: new Date().toISOString(),
      };

      let result;

      if (isEditMode && !isCloneMode) {
        // Update existing post
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', id as string); // Ensure id is treated as string
        if (error) throw error;
        result = { error: null }; // Simulate result structure for consistency
      } else {
        // Insert new post (create or clone)
        const { error } = await supabase
          .from('blog_posts')
          .insert([{ ...postData, created_at: new Date().toISOString() }]) // Add created_at for new posts
          .select() // Select to confirm insertion
          .single(); // Expect a single record
        if (error) throw error;
        result = { error: null }; // Simulate result structure for consistency
      }

      // This success toast/navigation happens only if no error was thrown above
      toast({
        title: isEditMode && !isCloneMode ? 'Post atualizado' : isCloneMode ? 'Post clonado' : 'Post criado',
        description: isEditMode && !isCloneMode 
          ? 'As alterações foram salvas com sucesso' 
          : isCloneMode
            ? 'O post foi clonado com sucesso'
            : 'O novo post foi criado com sucesso',
      });

      // Redirect to blog list after successful save
      navigate('/admin/blog');
    } catch (error: any) {
      console.error('Error saving post:', error);
      toast({
        title: 'Erro ao salvar post',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a function to manually add a tag
  const handleAddTag = () => {
    // Get the input element
    const tagInput = document.getElementById('new-tag') as HTMLInputElement;
    if (!tagInput || !tagInput.value.trim()) return;
    
    // Get the new tag and capitalize the first letter of each word
    const newTag = capitalizeWords(tagInput.value.trim());
    
    // Get current tags as an array
    const currentTags = formData.tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t !== '');
    
    // Check if the tag already exists
    if (currentTags.includes(newTag)) {
      toast({
        title: 'Tag já existe',
        description: `A tag "${newTag}" já está na lista.`,
        variant: 'destructive',
      });
      return;
    }
    
    // Add the new tag
    const updatedTags = [...currentTags, newTag].join(', ');
    setFormData(prev => ({ ...prev, tags: updatedTags }));
    
    // Clear the input
    tagInput.value = '';
  };

  if (initialLoading) {
    return (
      <AuthRequired>
        <AdminLayout>
          <div className="container mx-auto py-8 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </AdminLayout>
      </AuthRequired>
    );
  }

  const previewTags = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);

  return (
    <AuthRequired>
      <AdminLayout>
        {/* Add style tag for custom styles */}
        <style>{customStyles}</style>
        
        {/* Add keyframes for the spin animation */}
        <style>{keyframesStyle}</style>
        
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button 
                className="admin-button admin-button-outline mr-4"
                onClick={() => navigate('/admin/blog')}
              >
                <ChevronLeft size={16} />
              </button>
              <h1 className="text-3xl font-bold">
                {isEditMode ? 'Editar Post' : 'Novo Post'}
              </h1>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList>
                <TabsTrigger value="editor">Editor</TabsTrigger>
                <TabsTrigger value="preview">Visualizar</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <Tabs value={activeTab} className="w-full">
            <TabsContent value="editor">
              <Card>
                <form onSubmit={handleSubmit}>
                  <CardHeader>
                    <CardTitle>Detalhes do Post</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="title">Título</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleTitleChange}
                        required
                        placeholder="Título do post"
                        ref={titleInputRef}
                      />
                      <p className="text-xs text-gray-500">
                        O título do post será exibido como cabeçalho principal e nos resultados de busca.
                      </p>
                    </div>

                    {/* Slug field is now hidden and handled automatically */}
                    <input
                      type="hidden"
                      id="slug"
                      name="slug"
                      value={formData.slug}
                    />

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="excerpt">Resumo</Label>
                        <SuggestionButton
                          onClick={() => generateAISuggestion('excerpt')}
                          disabled={isGeneratingAI.excerpt}
                          isLoading={isGeneratingAI.excerpt}
                        />
                      </div>
                      <Textarea
                        id="excerpt"
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleInputChange}
                        required
                        placeholder="Breve resumo do post (exibido nas listagens)"
                        className="min-h-20"
                      />
                      <p className="text-xs text-gray-500">
                        Ideal entre 150-200 caracteres. Atual: {formData.excerpt.length}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="content">Conteúdo</Label>
                      <RichTextEditor
                        value={formData.content}
                        onChange={handleContentChange}
                        placeholder="Escreva o conteúdo completo do seu post aqui..."
                      />
                      <p className="text-xs text-gray-500">
                        Use as ferramentas de formatação para estilizar seu conteúdo.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="image">URL da imagem</Label>
                      <Input
                        id="image"
                        name="image"
                        value={formData.image}
                        onChange={handleInputChange}
                        required
                        placeholder="/assets/images/blog/imagem.jpg"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="author">Autor</Label>
                      <Input
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                        required
                        placeholder="Nome do autor"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
                        <SuggestionButton
                          onClick={() => generateAISuggestion('tags')}
                          disabled={isGeneratingAI.tags}
                          isLoading={isGeneratingAI.tags}
                          icon={Tag}
                          text="Sugerir 3 Tags"
                        />
                      </div>
                      <Input
                        id="tags"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        placeholder="Tag 1, Tag 2, Tag 3"
                        className="hidden"
                      />
                      <div className="flex flex-wrap gap-2 mt-2 min-h-10 p-2 border border-input bg-background rounded-md">
                        {formData.tags.split(',')
                          .map(tag => tag.trim())
                          .filter(tag => tag !== '')
                          .map((tag, index) => (
                            <Badge 
                              key={index} 
                              variant="secondary"
                              className="cursor-pointer bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800/40 px-3 py-1 text-sm font-medium shadow-sm"
                              onClick={() => handleTagClick(tag)}
                            >
                              {tag} <span className="ml-1 font-bold">×</span>
                            </Badge>
                          ))
                        }
                        {formData.tags.split(',').filter(tag => tag.trim() !== '').length === 0 && (
                          <span className="text-sm text-muted-foreground italic">Nenhuma tag adicionada</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          id="new-tag"
                          placeholder="Adicionar nova tag"
                          className="flex-1 border-blue-200 focus-visible:ring-blue-500/30 dark:border-blue-800/30 dark:focus-visible:ring-blue-500/20 capitalize"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                        />
                        <button 
                          type="button" 
                          onClick={handleAddTag}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: 'white',
                            color: '#374151',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          <Tag className="h-4 w-4" />
                          <span style={{ marginLeft: '0.25rem' }}>Adicionar Tag</span>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Clique em uma tag para removê-la. Recomendamos usar 3 tags específicas e relevantes para SEO.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="meta_description">Meta Descrição (SEO)</Label>
                        <SuggestionButton
                          onClick={() => generateAISuggestion('metaDescription')}
                          disabled={isGeneratingAI.metaDescription}
                          isLoading={isGeneratingAI.metaDescription}
                        />
                      </div>
                      <Textarea
                        id="meta_description"
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleInputChange}
                        required
                        placeholder="Descrição para SEO (ideal entre 150-160 caracteres)"
                        className="min-h-20"
                        maxLength={150}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          Ideal: 150 caracteres. Atual: {formData.meta_description.length}
                          {formData.meta_description.length > 145 && formData.meta_description.length <= 150 && (
                            <span className="text-green-500 ml-1">✓ Ótimo!</span>
                          )}
                          {formData.meta_description.length < 100 && (
                            <span className="text-amber-500 ml-1">Muito curto</span>
                          )}
                        </p>
                        <p className={`text-xs ${formData.meta_description.length >= 150 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                          {formData.meta_description.length}/150
                        </p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between card-footer-buttons">
                    <button 
                      type="button" 
                      className="admin-button admin-button-outline"
                      onClick={() => navigate('/admin/blog')}
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit"
                      className="admin-button admin-button-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-current rounded-full"></div>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="mr-2" />
                          Salvar
                        </>
                      )}
                    </button>
                  </CardFooter>
                </form>
              </Card>
            </TabsContent>
            <TabsContent value="preview" className="bg-white">
              <BlogPostPreview
                title={formData.title}
                excerpt={formData.excerpt}
                content={formData.content.join('')}
                tags={previewTags}
                image={formData.image}
                author={formData.author}
              />
            </TabsContent>
          </Tabs>
        </div>
      </AdminLayout>
    </AuthRequired>
  );
};
